import React, { useMemo, useState } from 'react';
import { Sparkles, Search, Check } from 'lucide-react';
import clsx from 'clsx';
import { WELCOME_PRESETS, CLASSIC_STYLE } from '../../../data/welcomeStyles';
import { WelcomeLottie } from '../view/StorePopup';

const CHARACTER_EMOJI = {
    fox: '🦊', bear: '🐻', bunny: '🐰', cat: '🐱', owl: '🦉', panda: '🐼',
    frog: '🐸', chick: '🐥', dog: '🐶', koala: '🐨', penguin: '🐧', monster: '👾',
};

/**
 * Lets the admin pick how the store welcome message is presented:
 *   - "קלאסי"  → the original static modal
 *   - "אנימציה" → one of 50 animated character presets
 *
 * Only the selected preset renders a live Lottie (in the preview box) so the
 * picker stays light even with 50 options.
 */
export function WelcomeStylePicker({ value, onChange }) {
    const isAnimated = value && value !== CLASSIC_STYLE;
    const [query, setQuery] = useState('');

    const filtered = useMemo(() => {
        const q = query.trim();
        if (!q) return WELCOME_PRESETS;
        return WELCOME_PRESETS.filter((p) => p.name.includes(q));
    }, [query]);

    const selectedPreset = isAnimated
        ? WELCOME_PRESETS.find((p) => p.id === value)
        : null;

    const pickFirstAnimated = () =>
        onChange(selectedPreset ? value : WELCOME_PRESETS[0].id);

    return (
        <div className="space-y-4">
            {/* Mode switch: classic vs animated */}
            <div className="grid grid-cols-2 gap-3">
                <button
                    type="button"
                    onClick={() => onChange(CLASSIC_STYLE)}
                    className={clsx(
                        'rounded-xl border p-4 text-start transition-colors',
                        !isAnimated
                            ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-200'
                            : 'border-line-subtle hover:bg-surface-muted',
                    )}
                >
                    <div className="flex items-center justify-between">
                        <span className="font-bold text-ink-strong">קלאסי</span>
                        {!isAnimated && <Check className="w-4 h-4 text-brand-600" />}
                    </div>
                    <p className="text-sm text-ink-muted mt-1">
                        חלונית פשוטה עם כותרת וטקסט (כמו עכשיו).
                    </p>
                </button>
                <button
                    type="button"
                    onClick={pickFirstAnimated}
                    className={clsx(
                        'rounded-xl border p-4 text-start transition-colors',
                        isAnimated
                            ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-200'
                            : 'border-line-subtle hover:bg-surface-muted',
                    )}
                >
                    <div className="flex items-center justify-between">
                        <span className="font-bold text-ink-strong inline-flex items-center gap-1.5">
                            <Sparkles className="w-4 h-4 text-secondary-500" /> עם אנימציה
                        </span>
                        {isAnimated && <Check className="w-4 h-4 text-brand-600" />}
                    </div>
                    <p className="text-sm text-ink-muted mt-1">
                        דמות חמודה שמופיעה עם ברכת קבלת הפנים. 50 סגנונות לבחירה.
                    </p>
                </button>
            </div>

            {isAnimated && (
                <div className="rounded-xl border border-line-subtle overflow-hidden">
                    {/* Live preview of the selected preset */}
                    <div className="bg-surface-muted/60 flex items-center justify-center h-44">
                        {selectedPreset && (
                            <WelcomeLottie
                                key={selectedPreset.id}
                                styleId={selectedPreset.id}
                                className="h-40 w-full max-w-xs"
                            />
                        )}
                    </div>
                    <div className="px-4 py-2.5 border-t border-line-subtle flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-ink-strong truncate">
                            {selectedPreset?.name}
                        </span>
                        <span className="text-xs text-ink-subtle shrink-0">
                            תצוגה מקדימה
                        </span>
                    </div>

                    {/* Search + grid of presets */}
                    <div className="p-3 border-t border-line-subtle space-y-3">
                        <div className="relative">
                            <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-ink-subtle" />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="חיפוש סגנון (למשל: שועל, בלונים)..."
                                className="w-full ps-9 pe-3 py-2 rounded-lg border border-line-subtle bg-surface text-sm focus:outline-none focus:ring-1 focus:ring-brand-300"
                            />
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto pe-1">
                            {filtered.map((p) => {
                                const active = p.id === value;
                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => onChange(p.id)}
                                        title={p.name}
                                        className={clsx(
                                            'rounded-lg border p-2.5 text-start transition-colors flex items-center gap-2',
                                            active
                                                ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-200'
                                                : 'border-line-subtle hover:bg-surface-muted',
                                        )}
                                    >
                                        <span className="text-xl leading-none shrink-0" aria-hidden>
                                            {CHARACTER_EMOJI[p.character] || '✨'}
                                        </span>
                                        <span className="text-xs font-medium text-ink-strong leading-tight line-clamp-2">
                                            {p.name}
                                        </span>
                                        {active && (
                                            <Check className="w-3.5 h-3.5 text-brand-600 ms-auto shrink-0" />
                                        )}
                                    </button>
                                );
                            })}
                            {filtered.length === 0 && (
                                <p className="col-span-full text-center text-sm text-ink-subtle py-4">
                                    לא נמצאו סגנונות תואמים.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default WelcomeStylePicker;
